import { useNavigate } from "react-router";
import Header from "@/sections/Header";
import {
  ArrowLeft, Droplets, ShieldCheck, BarChart3,
  Settings, Users, Save, Printer, AlertTriangle, FlaskConical,
  FileCheck, Layers, ChevronRight, MapPin,
  CheckCircle2, TrendingUp, FileText
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/* ─── Sub-components (defined outside to avoid re-creation on render) ─── */
function Section({ number, title, icon: Icon, children }: { number: string; title: string; icon: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="mb-12 scroll-mt-24" id={`step-${number}`}>
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-rb-active flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgba(30,58,95,0.25)]">
          <Icon className="w-5 h-5 text-rb-brand" />
        </div>
        <div>
          <div className="text-xs font-bold text-rb-brand uppercase tracking-wider mb-0.5">Step {number}</div>
          <h2 className="text-xl font-bold text-slate-200">{title}</h2>
        </div>
      </div>
      <div className="ml-16">
        {children}
      </div>
    </div>
  );
}

function InfoBox({ type = "info", children }: { type?: "info" | "warning" | "tip"; children: React.ReactNode }) {
  const styles = {
    info: "bg-rb-active/[0.04] border-rb-active/15 text-rb-active",
    warning: "bg-rb-brand/[0.04] border-rb-brand/20 text-rb-surface",
    tip: "bg-emerald-500/10 border-emerald-800/50 text-emerald-400",
  };
  return (
    <div className={`p-4 rounded-xl border text-sm leading-relaxed ${styles[type]}`}>
      {children}
    </div>
  );
}

function ParameterTable({ rows }: { rows: { param: string; unit: string; typical: string; note: string }[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800/60 shadow-[0_2px_8px_rgba(10,22,40,0.04)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-rb-elevated">
            <th className="text-left py-2.5 px-4 font-bold text-xs text-slate-200 uppercase">Parameter</th>
            <th className="text-left py-2.5 px-4 font-bold text-xs text-slate-200 uppercase">Unit</th>
            <th className="text-left py-2.5 px-4 font-bold text-xs text-slate-200 uppercase">Typical Range</th>
            <th className="text-left py-2.5 px-4 font-bold text-xs text-slate-200 uppercase">Why It Matters</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-800/40 hover:bg-rb-elevated transition-colors">
              <td className="py-2.5 px-4 font-semibold text-slate-200">{r.param}</td>
              <td className="py-2.5 px-4 text-slate-500">{r.unit}</td>
              <td className="py-2.5 px-4 text-slate-600">{r.typical}</td>
              <td className="py-2.5 px-4 text-slate-500">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InstructionsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-rb-elevated text-slate-300 font-sans">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-14">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-rb-active hover:bg-rb-elevated hover:border-rb-active/30 hover:shadow-sm transition-all mb-8 rb-card"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Calculator
          </button>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rb-active/[0.06] text-rb-active text-xs font-bold mb-4 border border-rb-active/10 uppercase tracking-wider">
            User Guide
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-200 mb-4">
            How to Use the Calculator
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg leading-relaxed">
            A step-by-step guide to getting the most accurate wastewater treatment assessment from your lab data.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="p-6 shadow-[0_4px_24px_rgba(10,22,40,0.06)] mb-14 rb-section">
          <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider mb-4">Contents</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { n: "1", label: "Set Discharge Limits" },
              { n: "2", label: "Enter Water Quality Data" },
              { n: "3", label: "Add Dairy / Brewery Streams" },
              { n: "4", label: "Validate Data & Check Dosing" },
              { n: "5", label: "Configure Chemical Dosing" },
              { n: "6", label: "Run the Assessment" },
              { n: "7", label: "Read the Results Tabs" },
              { n: "8", label: "Save Samples & Trend Reports" },
              { n: "9", label: "Save Assessment Results" },
              { n: "10", label: "User Accounts & Admin" },
              { n: "11", label: "Print & Export Reports" },
            ].map(item => (
              <button
                key={item.n}
                onClick={() => document.getElementById(`step-${item.n}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-200 hover:bg-rb-elevated hover:text-rb-active transition-all text-left group"
              >
                <span className="w-6 h-6 rounded-lg bg-rb-active text-white text-xs font-bold flex items-center justify-center shrink-0 group-hover:bg-rb-brand group-hover:text-slate-200 transition-colors">
                  {item.n}
                </span>
                {item.label}
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 ml-auto group-hover:text-rb-brand transition-colors" />
              </button>
            ))}
          </div>
        </div>

        {/* ─── Step 1 ─── */}
        <Section number="1" title="Set Your Discharge Limits" icon={ShieldCheck}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            Before entering any water quality data, configure the effluent discharge limits that apply to your site. These limits drive the compliance engine — every stage of the proposed treatment train is checked against these values.
          </p>
          <InfoBox type="info">
            <strong className="text-slate-200">Default limits are pre-loaded</strong> based on typical trade effluent standards (e.g. BOD 3000 mg/L, TSS 400 mg/L, pH 6.0–10.0). If your local authority or consent agreement specifies different values, update them before running the assessment.
          </InfoBox>
          <div className="mt-4">
            <h4 className="text-sm font-bold text-slate-200 mb-3">Key Limits to Review</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              {[
                { label: "BOD₅", val: "3000 mg/L" },
                { label: "COD", val: "5000 mg/L" },
                { label: "TSS", val: "400 mg/L" },
                { label: "pH Range", val: "6.0 – 10.0" },
                { label: "Temperature", val: "38°C max" },
                { label: "Conductivity", val: "2500 µS/cm" },
              ].map((l, i) => (
                <div key={i} className="p-3 shadow-[0_1px_4px_rgba(10,22,40,0.04)] rb-card">
                  <div className="text-xs text-slate-400 uppercase font-bold">{l.label}</div>
                  <div className="font-bold text-slate-200 mt-0.5">{l.val}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Click the <strong>Configure</strong> button on the Discharge Limits panel to expand it. Use <strong>Reset Defaults</strong> to restore the original values at any time.
          </p>
        </Section>

        {/* ─── Step 2 ─── */}
        <Section number="2" title="Enter Water Quality Parameters" icon={Droplets}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            Input your lab analysis data using the tabbed form. The calculator accepts values in standard SI units. Navigate between tabs using the underline-style tab bar at the top of the form.
          </p>
          <InfoBox type="warning">
            <strong>Minimum required inputs:</strong> Flow Rate (m³/day), BOD₅, COD, TSS, pH, and Temperature. The more complete your data, the more accurate the treatability classification and process selection will be.
          </InfoBox>
          <div className="mt-5">
            <h4 className="text-sm font-bold text-slate-200 mb-3">Parameter Guide by Tab</h4>
            <ParameterTable rows={[
              { param: "Flow Rate", unit: "m³/day", typical: "50 – 5,000", note: "Design capacity for plant sizing" },
              { param: "Conductivity", unit: "µS/cm", typical: "500 – 17,600", note: "Critical for salinity detection" },
              { param: "BOD₅", unit: "mg/L", typical: "50 – 20,500", note: "Organic load → biological stage size" },
              { param: "COD", unit: "mg/L", typical: "100 – 50,000", note: "Total oxidisable matter" },
              { param: "TSS", unit: "mg/L", typical: "50 – 10,900", note: "Physical solids → pre-treatment need" },
              { param: "pH", unit: "-", typical: "4.0 – 12.0", note: "Biological viability & neutralisation" },
              { param: "TDS", unit: "mg/L", typical: "200 – 15,000", note: "Dissolved salts → salinity assessment" },
              { param: "Oil & Grease", unit: "mg/L", typical: "5 – 500", note: "> 15 mg/L triggers DAF recommendation" },
              { param: "TKN / TN / TP", unit: "mg/L", typical: "5 – 200", note: "Nutrient removal stage sizing" },
              { param: "Temperature", unit: "°C", typical: "10 – 45", note: "Affects biological kinetics" },
            ]} />
          </div>
          <p className="text-sm text-slate-500 mt-4">
            The <strong>BOD/COD ratio</strong> is calculated automatically. A ratio above 0.5 indicates easily biodegradable wastewater; below 0.3 suggests difficult-to-treat or chemically complex effluent.
          </p>
        </Section>

        {/* ─── Step 3 ─── */}
        <Section number="3" title="Add Dairy or Brewery Effluent Streams" icon={Layers}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            If your wastewater includes effluent from a dairy processing plant or brewery, enable the corresponding toggle switches. The calculator will compute a <strong>weighted average blend</strong> of all streams and use the blended parameters for the assessment.
          </p>
          <InfoBox type="tip">
            <strong>Dairy streams</strong> typically have high fat, lactose, and protein content (BOD 1,000–3,000 mg/L). <strong>Brewery streams</strong> are extremely high in organics (BOD 2,000–10,000 mg/L) and often acidic. Blending changes the treatability profile significantly.
          </InfoBox>
          <div className="mt-4 space-y-3">
            <div className="p-4 shadow-[0_1px_4px_rgba(10,22,40,0.04)] rb-card">
              <h5 className="font-bold text-slate-200 text-sm mb-1">How Blending Works</h5>
              <p className="text-sm text-slate-500">
                Each parameter is computed as <code className="text-rb-active bg-rb-elevated px-1.5 py-0.5 rounded font-mono text-xs">Σ(flowᵢ × paramᵢ) / Σ(flowᵢ)</code>. The blended preview updates in real-time as you enter values. pH is calculated from weighted hydrogen ion concentrations.
              </p>
            </div>
          </div>
        </Section>

        {/* ─── Step 4 ─── */}
        <Section number="4" title="Validate Data & Check Dosing Requirements" icon={CheckCircle2}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            Before running the full assessment, click the <strong>Check All Data</strong> button. This opens a validation popup that previews your data, shows the likely process train, and recommends chemical dosing for each piece of equipment.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="p-4 shadow-[0_1px_4px_rgba(10,22,40,0.04)] text-center rb-card">
              <div className="w-10 h-10 rounded-xl bg-rb-active flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="w-5 h-5 text-rb-brand" />
              </div>
              <div className="text-sm font-bold text-slate-200">Data Check</div>
              <div className="text-xs text-slate-500 mt-1">Review blended parameters and data quality</div>
            </div>
            <div className="p-4 shadow-[0_1px_4px_rgba(10,22,40,0.04)] text-center rb-card">
              <div className="w-10 h-10 rounded-xl bg-rb-active flex items-center justify-center mx-auto mb-2">
                <FlaskConical className="w-5 h-5 text-rb-brand" />
              </div>
              <div className="text-sm font-bold text-slate-200">Dosing Setup</div>
              <div className="text-xs text-slate-500 mt-1">One-click apply recommended chemicals</div>
            </div>
            <div className="p-4 shadow-[0_1px_4px_rgba(10,22,40,0.04)] text-center rb-card">
              <div className="w-10 h-10 rounded-xl bg-rb-active flex items-center justify-center mx-auto mb-2">
                <Settings className="w-5 h-5 text-rb-brand" />
              </div>
              <div className="text-sm font-bold text-slate-200">Ready</div>
              <div className="text-xs text-slate-500 mt-1">Confirm and proceed to full assessment</div>
            </div>
          </div>
          <InfoBox type="info">
            The popup shows which equipment <strong>requires</strong> chemicals. DAF needs coagulant + flocculant. Lamella Clarifiers need coagulant. Sedimentation needs coagulant when TSS {'>'} 300 mg/L. Click the <strong>Apply</strong> buttons to auto-fill dosing, then run the assessment.
          </InfoBox>
        </Section>

        {/* ─── Step 5 ─── */}
        <Section number="5" title="Configure Chemical Dosing" icon={FlaskConical}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            Chemical dosing is <strong>required</strong> for effective operation of several treatment stages — particularly DAF (Dissolved Air Flotation), Lamella Clarifiers, and Tube Settlers. Without coagulants and flocculants, solids will not agglomerate into settleable floc, and removal efficiency drops dramatically.
          </p>
          <InfoBox type="warning">
            <strong>Lamella Clarifier requirement:</strong> Coagulant (Ferric Chloride, Aluminium Sulfate, or PAC) is essential. Polyelectrolyte flocculant is required for DAF to form stable bubble-floc attachment. The calculator will warn you if dosing is missing for stages that need it.
          </InfoBox>
          <div className="mt-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-4 rb-card">
                <h5 className="font-bold text-slate-200 text-sm mb-2">Recommended Approach</h5>
                <p className="text-sm text-slate-500">
                  Use the <strong>Check All Data</strong> popup to see auto-recommended chemicals and doses. Click any recommendation to auto-apply it. Recommended dosing lines are highlighted in the results.
                </p>
              </div>
              <div className="p-4 rb-card">
                <h5 className="font-bold text-slate-200 text-sm mb-2">Manual Configuration</h5>
                <p className="text-sm text-slate-500">
                  Click "Add Dosing Line", select a chemical from the dropdown, enable it, and adjust the dose using the slider or direct input. The daily consumption and pH impact preview updates automatically.
                </p>
              </div>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Look for <span className="text-red-600 font-bold">red REQUIRED badges</span> on dosing lines — these indicate which equipment in the process train needs that chemical.
          </p>
        </Section>

        {/* ─── Step 6 ─── */}
        <Section number="6" title="Run the Assessment" icon={Settings}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            Once all parameters and dosing are configured, click the <strong>Run Assessment</strong> button. The engine performs the following in real-time:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Classifies wastewater complexity (Simple → Very Complex)",
              "Screens for toxic compounds and biological inhibitors",
              "Checks biological viability against 11 criteria",
              "Selects optimal equipment from 40+ item library",
              "Simulates stage-by-stage mass balance",
              "Calculates tank volumes from flow rate and HRT",
              "Checks discharge compliance at every stage",
              "Generates salinity probability (PSS-78 scale)",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 p-3 shadow-[0_1px_4px_rgba(10,22,40,0.04)] rb-card">
                <div className="w-5 h-5 rounded-full bg-rb-brand text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <span className="text-sm text-slate-600">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-4">
            If no chemical dosing is configured, a warning will appear in the results. You can return to the Dosing section, add chemicals, and re-run without losing your input data.
          </p>
        </Section>

        {/* ─── Step 7 ─── */}
        <Section number="7" title="Read & Navigate the Results Tabs" icon={BarChart3}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            Results appear below the assessment button in a professional tabbed interface. Each tab focuses on a different aspect of the analysis. Click through all tabs for the complete picture.
          </p>
          <div className="space-y-3">
            {[
              { tab: "Overview", desc: "Blended stream summary, process train grid with compliance badges, and high-level recommendations." },
              { tab: "Process Train", desc: "Detailed stage-by-stage cards with mass balance, tank volumes, HRT, and arrow connectors showing flow direction." },
              { tab: "Assessment", desc: "Treatability index, toxicity screening, biological viability scores, and pre-treatment requirements." },
              { tab: "Salinity", desc: "PSS-78 Practical Salinity Scale calculation, conductivity-to-salinity conversion, and probability assessment." },
              { tab: "Filtration", desc: "Multimedia filter sizing, GAC recommendations, backwash calculations, and cloth filter assessment." },
              { tab: "Sample Trends", desc: "Appears when 3+ saved samples exist. Line and bar charts tracking BOD, COD, TSS, pH, and nutrients over time." },
              { tab: "My Results", desc: "Signed-in users only. Lists every server-saved assessment with date, location, ISO report number, and key parameters." },
              { tab: "Sample Reports", desc: "Signed-in users only. View and print ISO-numbered sample trend reports generated from 3+ saved samples." },
              { tab: "Print Report", desc: "Full A4-formatted treatment summary with all stages, limits, author name, sample history, and unique ISO report number." },
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-3.5 shadow-[0_1px_4px_rgba(10,22,40,0.04)] rb-card">
                <span className="px-2.5 py-1 rounded-lg bg-rb-active text-white text-xs font-bold shrink-0">{r.tab}</span>
                <span className="text-sm text-slate-600">{r.desc}</span>
              </div>
            ))}
          </div>
          <InfoBox type="tip">
            <strong>Compliance badges</strong> appear on every process stage card. A green <span className="text-emerald-600 font-bold">COMPLIANT</span> badge means all parameters at that stage are within your discharge limits. A red badge highlights which limit is exceeded.
          </InfoBox>
        </Section>

        {/* ─── Step 8 ─── */}
        <Section number="8" title="Save Samples & Generate Trend Reports" icon={TrendingUp}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            Use the <strong>Water Sample Results</strong> panel to save multiple samples over time. Each saved sample stores all input parameters with a name and date. This enables long-term trend analysis and ISO-numbered report generation.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="p-4 text-center rb-card">
              <div className="w-10 h-10 rounded-xl bg-rb-active flex items-center justify-center mx-auto mb-2">
                <Save className="w-5 h-5 text-rb-brand" />
              </div>
              <div className="text-sm font-bold text-slate-200">Save Current</div>
              <div className="text-xs text-slate-500 mt-1">Click "Manage" then "Save Current as New Sample"</div>
            </div>
            <div className="p-4 text-center rb-card">
              <div className="w-10 h-10 rounded-xl bg-rb-active flex items-center justify-center mx-auto mb-2">
                <FileText className="w-5 h-5 text-rb-brand" />
              </div>
              <div className="text-sm font-bold text-slate-200">3+ for Reports</div>
              <div className="text-xs text-slate-500 mt-1">Generate ISO-numbered A4 trend reports after 3 samples</div>
            </div>
            <div className="p-4 text-center rb-card">
              <div className="w-10 h-10 rounded-xl bg-rb-active flex items-center justify-center mx-auto mb-2">
                <ArrowLeft className="w-5 h-5 text-rb-brand" />
              </div>
              <div className="text-sm font-bold text-slate-200">Load & Amend</div>
              <div className="text-xs text-slate-500 mt-1">Click any saved sample to load, amend data, then save as new</div>
            </div>
          </div>
          <InfoBox type="info">
            <strong>Sample Trend Reports</strong> are generated from the Sample Manager when you have 3+ saved samples and are signed in. Each report receives a unique ISO number (format: <code className="font-mono text-xs">RB-SMP-YYYY-0001</code>), includes trend charts, a full data table, and the author's name. Reports are stored on the server and accessible from the <strong>Sample Reports</strong> tab.
          </InfoBox>
          <p className="text-sm text-slate-500 mt-4">
            You can also <strong>delete</strong> individual saved samples (trash icon on each card) and delete generated reports from the Sample Reports tab.
          </p>
        </Section>

        {/* ─── Step 9 ─── */}
        <Section number="9" title="Save Assessment Results to Server" icon={FileCheck}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            When signed in, a <strong>Save Result</strong> button appears in the Results tab bar. Clicking it stores the complete assessment — including parameters, blended values, limits, and simulation output — to the server with automatic geolocation capture and a unique ISO report number.
          </p>
          <InfoBox type="info">
            <strong>My Results tab</strong> appears for all signed-in users. It lists every server-saved assessment with date, location, ISO report number (format: <code className="font-mono text-xs">RB-TRT-YYYY-0001</code>), and key parameters. Geolocation is captured from your browser (with permission) to show where each assessment was performed.
          </InfoBox>
          <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
            <MapPin className="w-4 h-4 text-rb-brand" />
            Location data includes latitude, longitude, city, and country when available.
          </div>
        </Section>

        {/* ─── Step 10 ─── */}
        <Section number="10" title="User Accounts & Admin Dashboard" icon={Users}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            The calculator supports two user roles. All accounts require only a <strong>username and password</strong> — no personal name or email needed. However, you can optionally add your first and last name in your Profile. This name will appear on all printed reports.
          </p>
          <InfoBox type="tip">
            <strong>Optional name:</strong> Adding your first and last name is not mandatory. If left blank, reports will be generated without a named author. To add your name, click your user avatar → Profile, then enter your first and last name.
          </InfoBox>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="p-5 shadow-[0_2px_12px_rgba(10,22,40,0.06)] rb-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-rb-active flex items-center justify-center">
                  <Users className="w-4 h-4 text-rb-brand" />
                </div>
                <h4 className="font-bold text-slate-200">End User</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-rb-brand shrink-0 mt-0.5" />
                  Register with username + password
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-rb-brand shrink-0 mt-0.5" />
                  Save assessments and sample reports to server
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-rb-brand shrink-0 mt-0.5" />
                  View "My Results" and "Sample Reports" tabs
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-rb-brand shrink-0 mt-0.5" />
                  Optional: add name to profile for report authorship
                </li>
              </ul>
            </div>
            <div className="p-5 shadow-[0_2px_12px_rgba(10,22,40,0.06)] rb-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-rb-surface flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-rb-brand" />
                </div>
                <h4 className="font-bold text-slate-200">Administrator</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-rb-brand shrink-0 mt-0.5" />
                  Default login: admin / Babajaga1!23
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-rb-brand shrink-0 mt-0.5" />
                  Create and delete user accounts
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-rb-brand shrink-0 mt-0.5" />
                  View all user results, sample reports, dates & locations
                </li>
              </ul>
            </div>
          </div>
          <InfoBox type="warning">
            <strong>Security note:</strong> The admin password should be changed after first login. The admin dashboard is accessible at <code className="font-mono text-xs bg-rb-surface px-1.5 py-0.5 rounded">/admin</code>. Regular users should not access this route.
          </InfoBox>
        </Section>

        {/* ─── Step 11 ─── */}
        <Section number="11" title="Print & Export Reports" icon={Printer}>
          <p className="text-slate-600 mb-4 leading-relaxed">
            The calculator generates two types of professionally formatted, ISO-numbered A4 reports:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="p-5 shadow-[0_2px_12px_rgba(10,22,40,0.06)] rb-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-rb-active flex items-center justify-center">
                  <Printer className="w-4 h-4 text-rb-brand" />
                </div>
                <h4 className="font-bold text-slate-200">Assessment Report</h4>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Generated from the <strong>Print Report</strong> tab. Contains the complete treatment train, mass balances, tank volumes, compliance status, and dosing summary.
              </p>
              <div className="text-xs text-slate-400 font-mono bg-rb-elevated px-3 py-2 rounded-lg">
                Report #: RB-TRT-YYYY-0001
              </div>
            </div>
            <div className="p-5 shadow-[0_2px_12px_rgba(10,22,40,0.06)] rb-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-rb-surface flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-rb-brand" />
                </div>
                <h4 className="font-bold text-slate-200">Sample Trend Report</h4>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Generated from the <strong>Sample Manager</strong> when 3+ samples exist. Contains trend charts, data table, summary statistics, and sample history.
              </p>
              <div className="text-xs text-slate-400 font-mono bg-rb-elevated px-3 py-2 rounded-lg">
                Report #: RB-SMP-YYYY-0001
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Complete input water quality summary",
              "Proposed treatment train with all stages",
              "Tank volumes, HRT, and dimensions per stage",
              "Chemical dosing summary with daily consumption",
              "DAF sludge generation calculations",
              "Compliance status at every stage",
              "Discharge limits applied",
              "Sample history table (if 3+ samples saved)",
              "Unique ISO report number (sequential per year)",
              "Author name, date, time, and confidentiality notice",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 p-3 rb-card">
                <FileCheck className="w-4 h-4 text-rb-brand shrink-0 mt-0.5" />
                <span className="text-sm text-slate-600">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Click the <strong>Print A4 Report</strong> button to open the browser print dialog. Use "Save as PDF" for digital distribution. Both report types include Reynolds & Bauhm branding, are dated automatically, and display the author's name if set in Profile.
          </p>
        </Section>

        {/* ─── FAQ ─── */}
        <div className="mt-16 pt-10 border-t border-slate-800/60">
          <h2 className="text-2xl font-bold text-slate-200 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: "Do I need to enter every parameter?",
                a: "No. Flow Rate, BOD₅, COD, TSS, pH, and Temperature are the minimum. Missing parameters are treated as zero or estimated. The more complete your data, the more accurate the assessment.",
              },
              {
                q: "What is the 'Check All Data' button for?",
                a: "It validates your inputs, previews the likely process train, and suggests chemical dosing for each piece of equipment before you run the full assessment. You can apply recommendations with one click.",
              },
              {
                q: "What if my wastewater is saline or high-BOD?",
                a: "The calculator detects salinity from conductivity using the PSS-78 scale. For high-BOD (> 5,000 mg/L) or saline wastewater, the process library automatically selects compatible equipment including Halophilic MBBR, ABR, Wet Air Oxidation, and Forward Osmosis.",
              },
              {
                q: "Can I run the assessment without dosing?",
                a: "Yes, but a warning will appear. DAF, Lamella Clarifiers, and tube settlers require coagulants and flocculants to function. Without them, predicted removal efficiency is reduced and compliance may fail.",
              },
              {
                q: "How are tank volumes calculated?",
                a: "Tank volume = Flow Rate / 24 × HRT (hours). The HRT is specific to each process stage (e.g. 2 hr for equalisation, 4 hr for biological). Dimensions are estimated assuming a circular tank with 4 m working depth.",
              },
              {
                q: "Is my data saved on the server?",
                a: "Only if you are signed in and click Save Result (for assessments) or Generate Sample Trend Report (for sample reports). Guest users can use the calculator freely but results are not persisted. Saved results include geolocation for compliance tracking.",
              },
              {
                q: "What are the ISO report numbers?",
                a: "Every saved report gets a unique sequential number. Assessment reports use RB-TRT-YYYY-0001 format. Sample trend reports use RB-SMP-YYYY-0001 format. Numbers are assigned per year and never repeat.",
              },
              {
                q: "Can I use this for regulatory submission?",
                a: "The report provides a professional technical summary suitable for internal engineering review and preliminary client proposals. For formal regulatory submissions, always engage a chartered engineer to validate the design.",
              },
            ].map((faq, i) => (
              <div key={i} className="p-5 shadow-[0_2px_8px_rgba(10,22,40,0.04)] rb-section">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-rb-brand shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-slate-200 text-sm mb-2">{faq.q}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Back to Calculator CTA */}
        <div className="mt-16 text-center">
          <div className="rb-page-card p-8 text-white overflow-hidden relative">
            <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-rb-brand/[0.03] rounded-full blur-[100px] pointer-events-none" />
            <div className="relative">
              <h3 className="text-xl font-bold mb-2">Ready to Assess Your Wastewater?</h3>
              <p className="text-sm text-white/50 mb-6 max-w-lg mx-auto">
                Return to the calculator, enter your lab data, configure dosing, and receive a complete process train recommendation in seconds.
              </p>
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3.5 rounded-2xl bg-rb-brand text-slate-200 font-bold hover:bg-[#b8931f] hover:shadow-[0_8px_32px_rgba(205,164,46,0.3)] hover:scale-[1.03] transition-all shadow-lg inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Calculator
              </button>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center text-xs text-slate-400">
          <p>Reynolds & Bauhm — Water Treatment Engineering</p>
          <p className="mt-1">Reynolds & Bauhm — Water & Wastewater Treatment Specialists</p>
        </div>
      </main>
    </div>
  );
}
